/**
 * Pure-function suppression rules for the per-tile chip surface.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): when
 * `chipState !== null`, item.tsx suppresses the legacy badges that
 * the chip subsumes:
 *
 *   - Auto-tracking badge moves from top-RIGHT to top-LEFT (chip
 *     occupies top-right). Returned via `autoTrackingPositionClass`
 *     so item.tsx can compose with twMerge cleanly.
 *   - Existing `<Status>` badge is suppressed (the chip carries the
 *     per-aspect verdict redundantly otherwise).
 *   - Inline lock-credentials badge is suppressed (the chip's
 *     "Authenticate" verb covers it).
 *   - When chipState === null, every legacy behavior is unchanged —
 *     single-add regression-safe path.
 *
 * Per `think-in-systems` (ai.knowledge/global): extracting the
 * conditional decisions into pure functions lets the refactor be
 * tested without a full <Item> render — `item.tsx` consumes these
 * helpers and the suppression contract is verified here directly.
 *
 * Per `tests-require-user-story-comment` rule: the sibling
 * `suppression.test.ts` carries one user-story comment per branch.
 */

import type { ChipState } from './chip-state.js';

/**
 * Resolve the auto-tracking badge's horizontal position class. When
 * the chip is active (chipState !== null), the badge moves to the
 * top-left to free the top-right corner for the chip.
 *
 * Returns the position class only — the caller composes it with the
 * shared `absolute top-8` prefix.
 */
export const autoTrackingPositionClass = (chipState: ChipState | null): 'left-8' | 'right-8' =>
  chipState !== null ? 'left-8' : 'right-8';

/**
 * Whether the legacy `<Status>` badge should render. Suppressed when
 * the chip is active. When the chip is null, `<Status>` renders
 * unchanged — including for the bridge-offline state which the chip
 * cascade returns null for (so `<Status>` remains the surface there).
 */
export const shouldShowStatusBadge = (chipState: ChipState | null): boolean => chipState === null;

/**
 * Whether the inline lock-credentials badge should render. Suppressed
 * when the chip is active. Note: the caller still gates this on the
 * underlying authentication-status invalid condition — this helper
 * only encodes the chip-mediated suppression.
 */
export const shouldShowLockBadge = (chipState: ChipState | null): boolean => chipState === null;

/**
 * Whether the chip element itself should render in the single-
 * arrangement (`!isCollapsed`) layout. The chip text is hidden in
 * collapsed mode (the ring + tooltip carry the signal there).
 *
 * Returns true only when chipState is non-null AND the tile is in
 * single-arrangement mode.
 */
export const shouldShowChip = (chipState: ChipState | null, isCollapsed: boolean): boolean =>
  chipState !== null && !isCollapsed;
